import { useEffect, useState } from 'react'

/** A base-map tile source: a URL template plus the zoom range it serves. */
export interface BaseMapTileInfo {
  url: string
  generation: number
  minZ: number
  maxZ: number
}

const DEFAULT_MIN_ZOOM = 1
const DEFAULT_MAX_ZOOM = 19

/**
 * The base map the signed-in user is entitled to, from `/api/maps`.
 *
 * There is no static URL to configure: the endpoint returns whichever base maps
 * the caller may use, so every map surface has to ask. `ReportMap`, `StandsMap`
 * and `HuntReplayViewer` (all migrated onto the `@wehunt/map` MapLibre engine)
 * already use this hook rather than fetching inline; `teamMapPresentation` still
 * fetches `/api/maps` inline directly and is the remaining candidate to collapse
 * into this hook, most likely alongside its own eventual MapLibre migration.
 *
 * A failure is silent by design. Every caller draws its own content over the
 * tiles, and that content is still worth showing without terrain underneath —
 * an error page over a hunt whose pins loaded fine would be the worse outcome.
 */
export function useBaseMapTileInfo(): BaseMapTileInfo | null {
  const [tileInfo, setTileInfo] = useState<BaseMapTileInfo | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/maps')
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return
        // Two response shapes are in the wild: a `baseMaps` array, and a flat
        // object carrying `url` directly.
        const first = d.baseMaps?.[0] ?? (d.url ? d : null)
        if (!first?.url) return
        setTileInfo({
          url: first.url,
          generation: first.generation ?? 1,
          minZ: first.minZ ?? DEFAULT_MIN_ZOOM,
          maxZ: first.maxZ ?? DEFAULT_MAX_ZOOM,
        })
      })
      .catch(() => {
        // No base map. The caller's own overlay still renders.
      })
    return () => {
      cancelled = true
    }
  }, [])

  return tileInfo
}
